import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ArrowLeftIcon,
  PauseIcon,
  ChevronUpIcon,
  XIcon
} from 'lucide-react';
import { thingsboardService } from '../../services/thingsboard';
import { useTelemetry } from '../../hooks/useTelemetry';
import { useTranslation } from 'react-i18next';

interface Sensor {
  id: string;
  type: string;
  label: string;
  [key: string]: any;
}

interface SECDetailProps {
  sensor: Sensor;
  onBack: () => void;
  onClose: () => void;
}

export function SECDetail({ sensor, onBack, onClose, onShowToast }: SECDetailProps) {
  const { t } = useTranslation();
  const [percent, setPercent] = useState(100);
  const [hubSensorId, setHubSensorId] = useState<string | null>(null);

  // Parse dev_no from sensor label. e.g., SEC_2 -> 2, SEC_01 -> 1
  const devNo = useMemo(() => {
    const match = sensor.label.match(/SEC_0?(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }, [sensor.label]);

  // Fetch SEC_0 (Hub) on mount
  useEffect(() => {
    thingsboardService.getTenantDevice('SEC_0')
      .then(device => {
        if (device && device.id) {
          setHubSensorId(device.id.id);
        }
      })
      .catch(e => console.warn('Could not fetch SEC_0 hub device', e));
  }, []);

  // Use telemetry to get the real-time curtain_state
  const { data: realtimeData } = useTelemetry(sensor.id, ['curtain_state']);

  useEffect(() => {
    if (realtimeData && realtimeData['curtain_state']) {
      const stateArr = realtimeData['curtain_state'];
      if (stateArr.length > 0) {
        const val = Number(stateArr[0].value);
        if (!isNaN(val)) {
          setPercent(val);
        }
      }
    }
  }, [realtimeData]);

  // Request initial state from hub
  useEffect(() => {
    if (hubSensorId && devNo !== null) {
      thingsboardService.sendOneWayRpc(hubSensorId, 'get_curtain_state', { value: 0, dev_no: devNo })
        .catch(err => console.error('Failed to request initial get_curtain_state', err));
    }
  }, [hubSensorId, devNo]);

  const sendCommand = async (action: 'open' | 'close' | 'pause') => {
    if (!sensor.active) {
      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
      return;
    }
    if (!hubSensorId || devNo === null) return;

    try {
      console.log(`Sending ${action} command to hub:`, hubSensorId, 'for dev_no:', devNo);
      await thingsboardService.sendOneWayRpc(hubSensorId, 'set_curtain', { value: action, dev_no: devNo });
      
      // Optimistic UI updates
      if (action === 'open') setPercent(0);
      if (action === 'close') setPercent(100);
      
    } catch (err) {
      console.error(`Failed to send ${action} command`, err);
    }
  };

  const isCurtain = sensor.label === 'SEC_01';
  const subtitle = isCurtain ? t('sensor.smart_edge_controller') : t('sensor.smart_edge_controller');

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--mobile-bg)', color: 'var(--mobile-text-primary)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-16 border-b backdrop-blur-md sticky top-0 z-10"
        style={{ borderColor: 'var(--mobile-border)', background: 'var(--mobile-bg-header)' }}>
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--mobile-text-primary)' }}>{sensor.label}</h2>
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--mobile-text-muted)' }}>{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">

        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-8">
        {/* Visual representation */}
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-full max-w-[280px] aspect-square rounded-2xl border p-3 shadow-2xl" style={{ backgroundColor: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border-strong)' }}>
            <div className="absolute inset-4 rounded-xl border-2 border-slate-700/50 bg-gradient-to-b from-sky-900/20 to-transparent overflow-hidden">
              {/* Window background effect */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px] opacity-20" />
              
              {/* The actual curtain/blind */}
              <motion.div
                className="absolute top-0 left-0 right-0 bg-gradient-to-b from-amber-600 to-amber-800"
                initial={{ height: '100%' }}
                animate={{ height: `${percent}%` }}
                transition={{ type: 'spring', stiffness: 100, damping: 20 }}
              >
                {/* Horizontal slats for Blind */}
                {!isCurtain && Array.from({ length: 15 }).map((_, i) => (
                  <div key={i} className="w-full h-[6.66%] border-b border-black/20" />
                ))}
                
                {/* Bottom weight bar */}
                <div className="absolute bottom-0 left-0 right-0 h-2 bg-amber-500 shadow-[0_-2px_10px_rgba(0,0,0,0.5)]" />
                
                {/* Texture for curtain */}
                {isCurtain && (
                  <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:15px_100%] opacity-30" />
                )}
              </motion.div>
            </div>

            {/* Percentage Display */}
            <div className="absolute bottom-6 right-6 px-4 py-2 backdrop-blur-md rounded-2xl border shadow-xl"
              style={{ background: 'var(--mobile-bg-overlay)', borderColor: 'var(--mobile-border)' }}>
              <span className="text-3xl font-black font-mono" style={{ color: 'var(--mobile-text-primary)' }}>{percent}</span>
              <span className="text-sm ml-1" style={{ color: 'var(--mobile-text-muted)' }}>%</span>
            </div>
          </div>

          <div className="text-center">
            <div className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 mb-2">
              <span className="text-sm font-bold text-amber-400 uppercase tracking-widest">
                {percent >= 100 ? '닫힘' : percent <= 0 ? '열림' : '부분개방'}
              </span>
            </div>
            <p className="text-sm" style={{ color: 'var(--mobile-text-muted)' }}>{sensor.label} 장치의 현재 상태입니다.</p>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => sendCommand('open')}
              className="flex flex-col items-center justify-center gap-2 aspect-square rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 active:scale-95 transition-all"
              title="Open"
            >
              <div className="p-3 rounded-full bg-emerald-500/20">
                <ChevronUpIcon className="w-7 h-7" />
              </div>
              <span className="font-bold text-sm">올림</span>
            </button>

            <button
              onClick={() => sendCommand('pause')}
              className="flex flex-col items-center justify-center gap-2 aspect-square rounded-2xl bg-slate-500/10 border transition-all active:scale-95"
              style={{ borderColor: 'var(--mobile-border-strong)', color: 'var(--mobile-text-secondary)' }}
              title="Pause"
            >
              <div className="p-3 rounded-full bg-slate-500/20">
                <PauseIcon className="w-7 h-7 fill-current" />
              </div>
              <span className="font-bold text-sm">정지</span>
            </button>

            <button
              onClick={() => sendCommand('close')}
              className="flex flex-col items-center justify-center gap-2 aspect-square rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-500 active:scale-95 transition-all"
              title="Close"
            >
              <div className="p-3 rounded-full bg-amber-500/20">
                <ChevronDownIcon className="w-7 h-7" />
              </div>
              <span className="font-bold text-sm">내림</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
